import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VenueMap = ({ venues, selectedVenue, onVenueSelect, userLocation, mapCenter, onMapCenterChange }) => {
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [viewMode, setViewMode] = useState('hybrid'); // 'roadmap', 'satellite', 'hybrid', 'terrain'

  useEffect(() => {
    // Simulate map loading
    const timer = setTimeout(() => {
      setIsMapLoaded(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleVenueMarkerClick = (venue) => {
    onVenueSelect(venue);
  };

  const handleRecenterMap = () => {
    if (userLocation) {
      onMapCenterChange(userLocation);
    }
  };

  const getMapUrl = () => {
    const { lat, lng } = mapCenter;
    const zoom = 14;
    
    // Add venue markers to the map
    let markersParam = '';
    venues?.forEach((venue, index) => {
      if (index < 10) { // Limit markers to prevent URL length issues
        markersParam += `&markers=color:red%7Clabel:${index + 1}%7C${venue?.location?.lat},${venue?.location?.lng}`;
      }
    });

    // Add user location marker if available
    if (userLocation) {
      markersParam += `&markers=color:blue%7Clabel:U%7C${userLocation?.lat},${userLocation?.lng}`;
    }

    return `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dw901SwHSR3g&center=${lat},${lng}&zoom=${zoom}&maptype=${viewMode}${markersParam}`;
  };

  return (
    <div className="relative h-full bg-muted rounded-lg overflow-hidden">
      {/* Map Loading State */}
      {!isMapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando mapa...</p>
          </div>
        </div>
      )}
      {/* Google Maps Iframe */}
      <iframe
        width="100%"
        height="100%"
        loading="lazy"
        title="Mapa de Quadras Esportivas"
        referrerPolicy="no-referrer-when-downgrade"
        src={getMapUrl()}
        className={`transition-opacity duration-300 ${isMapLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setIsMapLoaded(true)}
      />
      {/* Map Controls */}
      <div className="absolute top-4 right-4 flex flex-col space-y-2 z-20">
        {/* View Mode Selector */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-1">
          <div className="flex flex-col space-y-1">
            {[
              { value: 'roadmap', label: 'Mapa', icon: 'Map' },
              { value: 'satellite', label: 'Satélite', icon: 'Satellite' },
              { value: 'hybrid', label: 'Híbrido', icon: 'Layers' }
            ]?.map((mode) => (
              <Button
                key={mode?.value}
                variant={viewMode === mode?.value ? "default" : "ghost"}
                size="xs"
                onClick={() => setViewMode(mode?.value)}
                iconName={mode?.icon}
                iconSize={14}
                className="text-xs"
              >
                {mode?.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Recenter Button */}
        {userLocation && (
          <Button
            variant="secondary"
            size="icon"
            onClick={handleRecenterMap}
            className="shadow-elevation-2"
            title="Centralizar na minha localização"
          >
            <Icon name="Navigation" size={16} />
          </Button>
        )}
      </div>
      {/* Venue Markers Info Panel */}
      <div className="absolute bottom-4 left-4 right-4 z-20">
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-card-foreground">
              Quadras no Mapa
            </h3>
            <span className="text-sm text-muted-foreground">
              {venues?.length} encontrada{venues?.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Legend */}
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error rounded-full"></div>
              <span className="text-muted-foreground">Quadras</span>
            </div>
            {userLocation && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Sua localização</span>
              </div>
            )}
          </div>

          {/* Selected Venue Info */}
          {selectedVenue && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden">
                  <img
                    src={selectedVenue?.images?.[0]}
                    alt={selectedVenue?.imageAlts?.[0]}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-card-foreground truncate">
                    {selectedVenue?.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedVenue?.address}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={12} className="text-warning fill-current" />
                      <span className="text-sm font-medium">{selectedVenue?.rating}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm font-data text-primary">
                      R$ {selectedVenue?.pricePerHour}/h
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => onVenueSelect(selectedVenue)}
                >
                  Ver Detalhes
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Map Attribution */}
      <div className="absolute bottom-1 right-1 text-xs text-muted-foreground bg-card/80 px-2 py-1 rounded">
        © Google Maps
      </div>
    </div>
  );
};

export default VenueMap;